import type { Document, DocumentType } from "~/interface/document";
import { getDocumentTypeName } from "~/interface/document";

/**
 * 整合性不整合の詳細情報
 */
export interface ConsistencyMismatch {
  documentType: DocumentType;
  referenceType: DocumentType;
  expectedId: string;
  actualId: string | undefined;
  message: string;
  documentModifiedAt?: string;
  referenceModifiedAt?: string;
}

/**
 * 整合性チェック結果
 */
export interface ConsistencyValidationResult {
  isConsistent: boolean;
  mismatches: ConsistencyMismatch[];
}

/**
 * 日時をJST形式でフォーマット (例: 1/28 12:30)
 */
export const formatDateTimeJST = (dateString?: string): string => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${month}/${day} ${hours}:${minutes}`;
};

/**
 * sourcesからの参照ID取得ヘルパー
 */
const getSourceId = (
  sources: Document["sources"] | undefined,
  type: string,
): string | undefined => {
  if (!sources || sources.length === 0) return undefined;
  return sources.find((s) => s.type === type)?.id;
};

/**
 * sourcesが空またはundefinedかどうかを判定
 */
const isSourcesEmpty = (sources: Document["sources"] | undefined): boolean => {
  return !sources || sources.length === 0;
};

/**
 * ドキュメントの更新日時を取得 (modified_at または created_at)
 */
const getDocumentModifiedAt = (doc: Document | null): string | undefined => {
  if (!doc) return undefined;
  return doc.modified_at || doc.created_at;
};

/**
 * ドキュメント間の整合性をチェック
 *
 * チェック対象:
 * 1. フィールド設計書 → アプリ設計書の参照
 * 2. カスタマイズ仕様書 → アプリ設計書の参照 (カスタマイズ仕様書がある場合のみ)
 * 3. カスタマイズ仕様書 → フィールド設計書の参照 (カスタマイズ仕様書がある場合のみ)
 */
export const validateDocumentConsistency = (
  appDesign: Document | null,
  fieldDesign: Document | null,
  customizeSpec: Document | null,
): ConsistencyValidationResult => {
  const mismatches: ConsistencyMismatch[] = [];

  // 前提条件チェック: アプリ設計書が存在しない
  if (!appDesign) {
    mismatches.push({
      documentType: "app-design",
      referenceType: "app-design",
      expectedId: "",
      actualId: undefined,
      message: `${getDocumentTypeName("app-design")}が存在しません`,
    });
    return { isConsistent: false, mismatches };
  }

  // 前提条件チェック: フィールド設計書が存在しない
  if (!fieldDesign) {
    mismatches.push({
      documentType: "field-design",
      referenceType: "field-design",
      expectedId: "",
      actualId: undefined,
      message: `${getDocumentTypeName("field-design")}が存在しません`,
    });
    return { isConsistent: false, mismatches };
  }

  // フィールド設計書のsourcesが空/undefined
  if (isSourcesEmpty(fieldDesign.sources)) {
    mismatches.push({
      documentType: "field-design",
      referenceType: "app-design",
      expectedId: appDesign.id,
      actualId: undefined,
      message: `${getDocumentTypeName("field-design")}の参照情報が存在しません`,
      documentModifiedAt: formatDateTimeJST(getDocumentModifiedAt(fieldDesign)),
      referenceModifiedAt: formatDateTimeJST(getDocumentModifiedAt(appDesign)),
    });
  } else {
    // フィールド設計書 → アプリ設計書の参照チェック
    const fieldDesignAppRef = getSourceId(fieldDesign.sources, "app-design");

    if (fieldDesignAppRef === undefined) {
      mismatches.push({
        documentType: "field-design",
        referenceType: "app-design",
        expectedId: appDesign.id,
        actualId: undefined,
        message: `${getDocumentTypeName("field-design")}の${getDocumentTypeName("app-design")}参照情報が見つかりません`,
        documentModifiedAt: formatDateTimeJST(getDocumentModifiedAt(fieldDesign)),
        referenceModifiedAt: formatDateTimeJST(getDocumentModifiedAt(appDesign)),
      });
    } else if (fieldDesignAppRef !== appDesign.id) {
      mismatches.push({
        documentType: "field-design",
        referenceType: "app-design",
        expectedId: appDesign.id,
        actualId: fieldDesignAppRef,
        message: `${getDocumentTypeName("field-design")}が参照している${getDocumentTypeName("app-design")}が、現在の${getDocumentTypeName("app-design")}と異なります`,
        documentModifiedAt: formatDateTimeJST(getDocumentModifiedAt(fieldDesign)),
        referenceModifiedAt: formatDateTimeJST(getDocumentModifiedAt(appDesign)),
      });
    }
  }

  // カスタマイズ仕様書がある場合のみチェック
  if (customizeSpec) {
    // カスタマイズ仕様書のsourcesが空/undefined
    if (isSourcesEmpty(customizeSpec.sources)) {
      mismatches.push({
        documentType: "customize-specification",
        referenceType: "app-design",
        expectedId: appDesign.id,
        actualId: undefined,
        message: `${getDocumentTypeName("customize-specification")}の参照情報が存在しません`,
        documentModifiedAt: formatDateTimeJST(getDocumentModifiedAt(customizeSpec)),
        referenceModifiedAt: formatDateTimeJST(getDocumentModifiedAt(appDesign)),
      });
    } else {
      // カスタマイズ仕様書 → アプリ設計書の参照チェック
      const customizeAppRef = getSourceId(customizeSpec.sources, "app-design");

      if (customizeAppRef === undefined) {
        mismatches.push({
          documentType: "customize-specification",
          referenceType: "app-design",
          expectedId: appDesign.id,
          actualId: undefined,
          message: `${getDocumentTypeName("customize-specification")}の${getDocumentTypeName("app-design")}参照情報が見つかりません`,
          documentModifiedAt: formatDateTimeJST(getDocumentModifiedAt(customizeSpec)),
          referenceModifiedAt: formatDateTimeJST(getDocumentModifiedAt(appDesign)),
        });
      } else if (customizeAppRef !== appDesign.id) {
        mismatches.push({
          documentType: "customize-specification",
          referenceType: "app-design",
          expectedId: appDesign.id,
          actualId: customizeAppRef,
          message: `${getDocumentTypeName("customize-specification")}が参照している${getDocumentTypeName("app-design")}が、現在の${getDocumentTypeName("app-design")}と異なります`,
          documentModifiedAt: formatDateTimeJST(getDocumentModifiedAt(customizeSpec)),
          referenceModifiedAt: formatDateTimeJST(getDocumentModifiedAt(appDesign)),
        });
      }

      // カスタマイズ仕様書 → フィールド設計書の参照チェック
      const customizeFieldRef = getSourceId(customizeSpec.sources, "field-design");

      if (customizeFieldRef === undefined) {
        mismatches.push({
          documentType: "customize-specification",
          referenceType: "field-design",
          expectedId: fieldDesign.id,
          actualId: undefined,
          message: `${getDocumentTypeName("customize-specification")}の${getDocumentTypeName("field-design")}参照情報が見つかりません`,
          documentModifiedAt: formatDateTimeJST(getDocumentModifiedAt(customizeSpec)),
          referenceModifiedAt: formatDateTimeJST(getDocumentModifiedAt(fieldDesign)),
        });
      } else if (customizeFieldRef !== fieldDesign.id) {
        mismatches.push({
          documentType: "customize-specification",
          referenceType: "field-design",
          expectedId: fieldDesign.id,
          actualId: customizeFieldRef,
          message: `${getDocumentTypeName("customize-specification")}が参照している${getDocumentTypeName("field-design")}が、現在の${getDocumentTypeName("field-design")}と異なります`,
          documentModifiedAt: formatDateTimeJST(getDocumentModifiedAt(customizeSpec)),
          referenceModifiedAt: formatDateTimeJST(getDocumentModifiedAt(fieldDesign)),
        });
      }
    }
  }

  return {
    isConsistent: mismatches.length === 0,
    mismatches,
  };
};
